import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaEye, FaCheck, FaTimes } from "react-icons/fa";
import { AdminAffiliateApiService } from "../../../service/admin/adminAffiliateApiService";
import ExcelTable from "../../../component/library/excelTable";
import "./WithdrawalApprovals.css";

const formatDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "-" : dt.toLocaleDateString();
};

const normalizeWithdrawal = (w) => {
  const affiliateObj = w?.affiliateId && typeof w.affiliateId === "object" ? w.affiliateId : null;

  const affiliateName = affiliateObj
    ? `${affiliateObj.firstName || ""} ${affiliateObj.lastName || ""}`.trim()
    : (w?.affiliate || "").trim();

  const affiliateCode =
    affiliateObj?.affiliateReferralCode ||
    w?.affiliateReferralCode ||
    w?.affiliateId ||
    "";

  const email = affiliateObj?.email || w?.email || "";

  const amount = Number(w?.amount ?? w?.requestedAmount ?? w?.withdrawAmount ?? 0) || 0;

  return {
    id: w?.id || w?._id || "",
    affiliateId: affiliateObj || w?.affiliateId || null, // keep raw for render
    amount,
    status: w?.status || "Pending",
    requestDate: w?.requestDate || w?.createdAt || null,
    processedDate: w?.processedDate || null,
    withdrawalMethod: w?.withdrawalMethod || w?.method || "-",
    rejectionReason: w?.rejectionReason || "",
    affiliateName: affiliateName || "Unknown",
    affiliateCode: affiliateCode || "—",
    email: email || "—",
  };
};

export default function WithdrawalApprovals() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState({ start: "", end: "" });

  const fetchWithdrawals = useCallback(async () => {
    try {
      setLoading(true);

      const params = { page, limit };
      if (searchTerm?.trim()) params.search = searchTerm.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      if (dateRangeFilter.start) params.startDate = dateRangeFilter.start;
      if (dateRangeFilter.end) params.endDate = dateRangeFilter.end;

      const response = await AdminAffiliateApiService.getWithdrawalRequests(params);

      const list = response?.data?.withdrawals || [];
      const normalized = (Array.isArray(list) ? list : []).map(normalizeWithdrawal);

      setWithdrawals(normalized);
      setPagination(
        response?.data?.pagination || {
          currentPage: page,
          totalPages: 1,
          totalItems: normalized.length,
        }
      );
    } catch (err) {
      console.error("Error fetching withdrawal requests:", err);
      setWithdrawals([]);
      setPagination({ currentPage: 1, totalPages: 1, totalItems: 0 });
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm, statusFilter, dateRangeFilter]);

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  const handleApprove = async (id) => {
    try {
      await AdminAffiliateApiService.approveWithdrawal(id);
      fetchWithdrawals();
    } catch (error) {
      console.error("Error approving withdrawal:", error);
    }
  };

  const handleReject = async (id) => {
    const reason = prompt("Please enter reason for rejection:");
    if (!reason?.trim()) return;

    try {
      await AdminAffiliateApiService.rejectWithdrawal(id, reason.trim());
      fetchWithdrawals();
    } catch (error) {
      console.error("Error rejecting withdrawal:", error);
    }
  };

  const tableData = useMemo(() => withdrawals, [withdrawals]);

  const columns = [
    {
      key: "sno",
      label: "S.No",
      width: 60,
      render: (_, __, index) => {
        const currentPage = pagination?.currentPage || 1;
        const limit = pagination?.itemsPerPage || 10;
        return (currentPage - 1) * limit + index + 1;
      },
    },
    {
      key: "affiliateId",
      label: "Affiliate",
      width: 200,
      render: (_, row) => (
        <div className="affiliate-withdrawal-info">
          <div className="affiliate-withdrawal-name">{row.affiliateName}</div>
          <div className="affiliate-withdrawal-email">{row.email}</div>
        </div>
      ),
    },
    {
      key: "amount",
      label: "Requested",
      width: 120,
      render: (value) => (
        <span className="affiliate-withdrawal-amount">₹{Number(value || 0).toLocaleString()}</span>
      ),
    },
    {
      key: "requestDate",
      label: "Date",
      width: 130,
      render: (value) => formatDate(value),
    },
    {
      key: "status",
      label: "Status",
      width: 120,
      render: (value) => {
        const statusClass = String(value || "pending").toLowerCase();
        return <span className={`affiliate-withdrawal-status ${statusClass}`}>{value || "Pending"}</span>;
      },
    },
    {
      key: "actions",
      label: "Actions",
      width: 180,
      render: (_, row) => (
        <div className="affiliate-withdrawal-actions">
          {String(row?.status || "").toLowerCase() === "pending" && (
            <div className="affiliate-action-buttons">
              <button
                className="affiliate-btn-approve"
                type="button"
                onClick={() => handleApprove(row.id)}
              >
                Approve
              </button>
              <button
                className="affiliate-btn-reject"
                type="button"
                onClick={() => handleReject(row.id)}
              >
                Reject
              </button>
            </div>
          )}
          {String(row?.status || "").toLowerCase() === "approved" && (
            <span className="affiliate-status-text">Paid</span>
          )}
          {String(row?.status || "").toLowerCase() === "rejected" && (
            <span className="affiliate-status-text">Rejected</span>
          )}
        </div>
      ),
    },
  ];

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleDateFilterChange = (e) => {
    setDateFilter(e.target.value);
    setPage(1);
  };

  const handleDateRangeChange = (type, value) => {
    setDateRangeFilter(prev => ({ ...prev, [type]: value }));
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  return (
    <div className="affiliate-withdrawal-approvals">
      <div className="affiliate-withdrawal-header">
        <h1>Withdrawal Approvals</h1>
        <p>Manage and approve affiliate withdrawal requests</p>
      </div>

      <div className="affiliate-withdrawal-filters">
        <div className="affiliate-filter-group">
          <label>Search:</label>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search by name, email..."
            className="affiliate-search-input"
          />
        </div>
        <div className="affiliate-filter-group">
          <label>Status:</label>
          <select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            className="affiliate-status-filter-select"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="affiliate-filter-group affiliate-date-range-group">
          <label>Date Range:</label>
          <div className="affiliate-date-range-inputs">
            <input
              type="date"
              value={dateRangeFilter.start}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              className="affiliate-date-filter-input"
              placeholder="Start Date"
            />
            <span className="affiliate-date-separator">to</span>
            <input
              type="date"
              value={dateRangeFilter.end}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              className="affiliate-date-filter-input"
              placeholder="End Date"
            />
          </div>
        </div>
      </div>

      <ExcelTable
        isLoading={loading}
        loadingText="Loading withdrawal requests..."
        data={tableData}
        columns={columns}
        title="Withdrawal Requests"
        pagination={pagination}
        onPageChange={setPage}
        onLimitChange={setLimit}
        searchable
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        selectable={false}
        sortable
        resizable
        showToolbar
        showToolbarExport
        showToolbarColumns
        showToolbarFullscreen
        showRefresh
        onRefresh={fetchWithdrawals}
        showRowsPerPage
      />
    </div>
  );
}